import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { useCatalogueContext } from '@/lib/catalogue-context'

interface TrainingOffer {
  id: string
  title: string
  description: string
  topic: string
  durationH: number
  price: number
  maxPax: number | null
  isRemote: boolean
  city: string | null
  status: string
  provider: { id: string; firstName: string; lastName: string; rating: number; ratingCount: number; specialties: string[] } | null
  company: { id: string; name: string; logoUrl: string | null } | null
  dates: { id: string; date: string }[]
  _count: { registrations: number }
}

function fmt(cents: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export default function CatalogueDetailPage() {
  const { hasNav } = useCatalogueContext()
  const { id } = useParams<{ id: string }>()
  const [offer, setOffer] = useState<TrainingOffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' })
  const [registering, setRegistering] = useState(false)
  const [registered, setRegistered] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    apiFetch<TrainingOffer>(`/catalogue/offers/${id}`)
      .then(setOffer)
      .catch(() => setOffer(null))
      .finally(() => setLoading(false))
  }, [id])

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setRegistering(true)
    try {
      await apiFetch(`/catalogue/offers/${id}/register`, {
        method: 'POST',
        body: JSON.stringify(form),
      })
      setRegistered(true)
    } catch (e: unknown) { setError((e as Error).message) }
    finally { setRegistering(false) }
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
    </div>
  )

  if (!offer) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="glass-card p-10 text-center">
        <p className="text-text-secondary">Formation introuvable.</p>
        <Link to="/catalogue" className="text-accent text-sm mt-2 block hover:underline">← Retour au catalogue</Link>
      </div>
    </div>
  )

  const spotsLeft = offer.maxPax ? offer.maxPax - offer._count.registrations : null
  const futureDates = offer.dates.filter((d) => new Date(d.date) >= new Date()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  return (
    <div className="min-h-screen" style={hasNav ? undefined : { background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      {!hasNav && (
        <header className="border-b border-white/[0.08] px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-text-primary font-bold text-xl tracking-tight">Velink</Link>
          <Link to="/catalogue" className="text-text-secondary text-sm hover:text-text-primary transition-colors">← Retour au catalogue</Link>
        </header>
      )}

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="md:col-span-2 space-y-5">
            <div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 border border-accent/20 text-accent mb-3 inline-block">{offer.topic}</span>
              <h1 className="text-3xl font-bold text-text-primary">{offer.title}</h1>
            </div>

            <div className="glass-card p-5">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Description</h2>
              <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">{offer.description}</p>
            </div>

            <div className="glass-card p-5">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Informations pratiques</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-text-muted text-xs mb-0.5">Durée</p>
                  <p className="text-text-primary font-medium">{offer.durationH}h</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs mb-0.5">Modalité</p>
                  <p className="text-text-primary font-medium">{offer.isRemote ? 'Distanciel' : `Présentiel — ${offer.city}`}</p>
                </div>
                {spotsLeft !== null && (
                  <div>
                    <p className="text-text-muted text-xs mb-0.5">Places disponibles</p>
                    <p className={`font-medium ${spotsLeft <= 3 ? 'text-danger' : 'text-text-primary'}`}>{spotsLeft} place{spotsLeft !== 1 ? 's' : ''}</p>
                  </div>
                )}
              </div>
            </div>

            {futureDates.length > 0 && (
              <div className="glass-card p-5">
                <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Prochaines dates</h2>
                <div className="flex flex-wrap gap-2">
                  {futureDates.map((d) => (
                    <span key={d.id} className="px-3 py-1.5 rounded-xl bg-accent/10 border border-accent/20 text-accent text-sm">
                      {new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Formateur / Organisme */}
            <div className="glass-card p-5">
              <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                {offer.provider ? 'Formateur' : 'Organisme de formation'}
              </h2>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent font-bold">
                    {offer.provider
                      ? `${offer.provider.firstName[0]}${offer.provider.lastName[0]}`
                      : (offer.company?.name[0] ?? '?')}
                  </span>
                </div>
                <div>
                  {offer.provider ? (
                    <>
                      <p className="text-text-primary font-semibold">{offer.provider.firstName} {offer.provider.lastName}</p>
                      <p className="text-text-muted text-xs">
                        {offer.provider.ratingCount > 0
                          ? `⭐ ${offer.provider.rating.toFixed(1)} (${offer.provider.ratingCount} avis)`
                          : 'Pas encore d\'avis'}
                      </p>
                      {offer.provider.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {offer.provider.specialties.map((s) => (
                            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/[0.08] text-text-muted">{s}</span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-text-primary font-semibold">{offer.company?.name ?? 'Centre de formation'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Colonne inscription */}
          <div className="space-y-4">
            <div className="glass-card p-5 sticky top-6">
              <div className="text-center mb-5">
                <p className="text-3xl font-bold text-accent">{fmt(offer.price)}</p>
                <p className="text-text-muted text-xs">par personne</p>
              </div>

              {offer.status === 'FULL' ? (
                <div className="text-center py-3 rounded-xl bg-danger/10 border border-danger/20 text-danger text-sm">
                  Formation complète
                </div>
              ) : offer.status === 'CANCELLED' ? (
                <div className="text-center py-3 rounded-xl bg-white/[0.06] border border-white/[0.12] text-text-muted text-sm">
                  Formation annulée
                </div>
              ) : registered ? (
                <div className="text-center py-4">
                  <p className="text-success text-2xl mb-2">✓</p>
                  <p className="text-success font-semibold text-sm">Inscription enregistrée !</p>
                  <p className="text-text-muted text-xs mt-1">Vous recevrez une confirmation par email.</p>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-3">
                  <h3 className="text-text-primary text-sm font-semibold mb-3">S'inscrire</h3>
                  <input required className="glass-input text-sm w-full" placeholder="Prénom *" value={form.firstName} onChange={(e) => setForm((v) => ({ ...v, firstName: e.target.value }))} />
                  <input required className="glass-input text-sm w-full" placeholder="Nom *" value={form.lastName} onChange={(e) => setForm((v) => ({ ...v, lastName: e.target.value }))} />
                  <input required type="email" className="glass-input text-sm w-full" placeholder="Email *" value={form.email} onChange={(e) => setForm((v) => ({ ...v, email: e.target.value }))} />
                  <input type="tel" className="glass-input text-sm w-full" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm((v) => ({ ...v, phone: e.target.value }))} />
                  {error && <p className="text-danger text-xs">{error}</p>}
                  <button type="submit" disabled={registering} className="glass-btn-accent w-full py-3 text-sm rounded-xl">
                    {registering ? 'Inscription…' : 'S\'inscrire'}
                  </button>
                  <p className="text-text-muted text-[10px] text-center">Le prestataire vous contactera pour confirmation.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
